import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Navbar from '../layout/Navbar';
import AddressTab from './tabs/AddressTab';
import ContactTab from './tabs/ContactTab';
import DestinationTab from './tabs/DestinationTab';
import DocumentTab from './tabs/DocumentTab';
import {
  ACCOUNT_DETAILS_ENDPOINT,
  ACCOUNT_UPDATE_ENDPOINT,
  PINCODE_LOOKUP_ENDPOINT,
  ACCOUNT_DUPLICATE_CHECK_ENDPOINT
} from '../../config/api';
import { decryptData } from '../../utils/encrypt';
import Footer from '../layout/Footer';

function AccountEdit() {
  const { id, token } = useParams();
  const navigate = useNavigate();

  const tabs = ['address', 'contact', 'destination', 'documents'];
  const [activeTab, setActiveTab] = useState('address');
  const [accountExists, setAccountExists] = useState(false);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    account_name: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    country: 'India',
    mobile: '',
    email: '',
    contacts: [],
    destinations: [],
    documents: []
  });

  useEffect(() => {
    const fetchAccount = async () => {
      try {
        const encryptedUser = localStorage.getItem('suyog_user');
        if (!encryptedUser) {
          alert('User not authenticated');
          return;
        }

        const { user_id, user_token } = decryptData(encryptedUser);

        const res = await fetch(ACCOUNT_DETAILS_ENDPOINT, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id, account_token: token, user_id, user_token })
        });

        const data = await res.json();

        if (res.ok && data.account) {
          const acc = data.account;
          setForm({
            ...acc,
            contacts: Array.isArray(acc.contacts) ? acc.contacts : [],
            destinations: Array.isArray(acc.destinations) ? acc.destinations : [],
            documents: Array.isArray(acc.documents) ? acc.documents : []
          });
        } else {
          alert(data.error || 'Failed to load account');
        }
      } catch (err) {
        console.error('Error fetching account:', err);
        alert('Server error');
      } finally {
        setLoading(false);
      }
    };

    fetchAccount();
  }, [id, token]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleContactChange = (index, field, value) => {
    const updated = [...form.contacts];
    updated[index][field] = value;
    setForm({ ...form, contacts: updated });
  };

  const handleDestinationChange = (index, field, value) => {
    const updated = [...form.destinations];
    updated[index][field] = value;
    setForm({ ...form, destinations: updated });
  };

  const setDocuments = (updatedDocs) => {
    setForm(prev => ({ ...prev, documents: updatedDocs }));
  };

  const handlePincodeBlur = async () => {
    if (form.pincode.length === 6) {
      try {
        const res = await fetch(`${PINCODE_LOOKUP_ENDPOINT}?pincode=${form.pincode}`);
        const data = await res.json();
        if (data.Status === 'Success' && data.PostOffice?.length) {
          const post = data.PostOffice[0];
          setForm(prev => ({
            ...prev,
            city: post.Name || '',
            state: post.State || '',
            country: post.Country || 'India'
          }));
        }
      } catch (err) {
        console.error('Error fetching pincode:', err);
      }
    }
  };

  const handleAccountNameBlur = async () => {
    const trimmedName = form.account_name.trim();
    if (trimmedName.length === 0) return;

    try {
      const res = await fetch(ACCOUNT_DUPLICATE_CHECK_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ account_name: trimmedName })
      });

      const data = await res.json();
      setAccountExists(data.exists);
    } catch (err) {
      console.error('Error checking account name:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const encryptedUser = localStorage.getItem('suyog_user');
      if (!encryptedUser) {
        alert('User not authenticated');
        return;
      }
      const { user_id, user_token } = decryptData(encryptedUser);

      const cleanContacts = form.contacts.map(contact => ({
        ...contact,
        email: contact.email || ''
      }));

      const payload = {
        ...form,
        contacts: cleanContacts,
        id,
        account_token: token,
        user_id,
        user_token,
        documents: form.documents.filter(doc => doc.id)
      };

      const res = await fetch(ACCOUNT_UPDATE_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const data = await res.json();
      if (res.ok && data.success) {
        alert('Account updated successfully');
        navigate(-1);
      } else {
        alert(data.error || 'Failed to update');
      }
    } catch (err) {
      console.error('Update error:', err);
      alert('Server error');
    }
  };

  const currentIndex = tabs.indexOf(activeTab);
  const goNext = () => setActiveTab(tabs[currentIndex + 1]);
  const goPrevious = () => setActiveTab(tabs[currentIndex - 1]);

  if (loading) return <div className="container mt-4">Loading...</div>;

  return (
    <>
      <Navbar />
      <div className="container mt-5">
        <div className="card shadow-lg border-0 rounded-4">
          <div className="card-header text-white rounded-top-4" style={{ background: 'linear-gradient(90deg, #007bff, #0056b3)', padding: '1rem 1.5rem' }}>
            <h4 className="mb-0">Edit Account</h4>
            <ul className="nav nav-tabs card-header-tabs mt-3">
              {tabs.map(tab => (
                <li className="nav-item" key={tab}>
                  <button
                    type="button"
                    className={`nav-link ${activeTab === tab ? 'active' : 'text-white-50'}`}
                    onClick={() => setActiveTab(tab)}
                    style={{ background: activeTab === tab ? '#ffffff' : 'transparent', border: 'none', borderRadius: '0.5rem 0.5rem 0 0', fontWeight: '500' }}
                  >
                    {tab.charAt(0).toUpperCase() + tab.slice(1)}
                  </button>
                </li>
              ))}
            </ul>
          </div>
          <div className="card-body p-4">
            <form onSubmit={handleSubmit}>
              {activeTab === 'address' && (
                <AddressTab
                  form={form}
                  handleChange={handleChange}
                  handlePincodeBlur={handlePincodeBlur}
                  handleAccountNameBlur={handleAccountNameBlur}
                  accountExists={accountExists}
                />
              )}
              {activeTab === 'contact' && (
                <ContactTab
                  contacts={form.contacts}
                  handleContactChange={handleContactChange}
                  addContact={() => setForm(prev => ({ ...prev, contacts: [...prev.contacts, { contact_person: '', phone1: '', phone2: '', department: '', email: '' }] }))}
                  removeContact={(index) => setForm(prev => {
                    const updated = [...prev.contacts];
                    updated.splice(index, 1);
                    return { ...prev, contacts: updated };
                  })}
                />
              )}
              {activeTab === 'destination' && (
                <DestinationTab
                  destinations={form.destinations}
                  handleDestinationChange={handleDestinationChange}
                  addDestination={() => setForm(prev => ({ ...prev, destinations: [...prev.destinations, { destination_address: '', remark: '', destination_city: '' }] }))}
                  removeDestination={(index) => setForm(prev => {
                    const updated = [...prev.destinations];
                    updated.splice(index, 1);
                    return { ...prev, destinations: updated };
                  })}
                />
              )}
              {activeTab === 'documents' && (
                <DocumentTab
                  documents={form.documents}
                  setDocuments={setDocuments}
                />
              )}
              <div className="d-flex justify-content-between mt-4">
                <button type="button" className="btn btn-outline-secondary" onClick={goPrevious} disabled={currentIndex === 0}>Previous</button>
                {currentIndex < tabs.length - 1 && (
                  <button type="button" className="btn btn-primary" onClick={goNext}>Next</button>
                )}
                {currentIndex === tabs.length - 1 && (
                  <button type="submit" className="btn btn-success">Update Account</button>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
}

export default AccountEdit;
